// src/screens/AdvertiserDashboardScreen.js
// ============================================================================
// ADVERTISER DASHBOARD - Campaign management portal for advertisers
//
// This is the authenticated advertiser hub where ad partners can:
// - View campaign performance (impressions, clicks, CTR, spend)
// - Create and manage ad campaigns
// - Upload and manage ad creatives
// - Submit campaigns for review
// - View their account details
// - (Admin) Review pending campaigns/creatives, see all advertiser stats
//
// LEGAL COMPLIANCE: Per CUSTODIAL_TERMINOLOGY_GUIDE.md
// - Advertisers purchase AD PLACEMENTS only
// - "Tournament Branding" NOT "Tournament Sponsorship"
// - No prizes; advertisers never fund creator compensation or influence outcomes
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView,
  TouchableOpacity, ActivityIndicator, Modal, TextInput,
  RefreshControl, Dimensions,
} from 'react-native';
import { onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../../firebaseConfig';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// ============================================================================
// CONSTANTS
// ============================================================================

const CAMPAIGN_STATUS_LABELS = {
  draft: 'Draft',
  pending_review: 'Under Review',
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  rejected: 'Rejected',
};

const CAMPAIGN_STATUS_COLORS = {
  draft: '#888',
  pending_review: '#F39C12',
  active: '#2ECC71',
  paused: '#E67E22',
  completed: '#3498DB',
  rejected: '#E74C3C',
};

const CREATIVE_STATUS_LABELS = {
  pending: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
  archived: 'Archived',
};

const CREATIVE_STATUS_COLORS = {
  pending: '#F39C12',
  approved: '#2ECC71',
  rejected: '#E74C3C',
  archived: '#888',
};

const PLACEMENT_LABELS = {
  lobby_banner: 'Lobby Banner',
  lobby_inline: 'Lobby Inline',
  live_banner: 'Live Matches Banner',
  live_inline: 'Live Inline',
  stream_preroll: 'Stream Pre-Roll',
  stream_overlay: 'Stream Overlay',
  event_branding: 'Tournament Branding',
  bracket_banner: 'Bracket Banner',
};

const PLACEMENT_OPTIONS = Object.entries(PLACEMENT_LABELS).map(([value, label]) => ({ value, label }));

const LOGO_ICON_OPTIONS = [
  { name: 'gamepad-variant', label: 'Gamepad' },
  { name: 'lightning-bolt', label: 'Lightning' },
  { name: 'seat', label: 'Chair' },
  { name: 'forum', label: 'Chat' },
  { name: 'headphones', label: 'Headphones' },
  { name: 'headset', label: 'Headset' },
  { name: 'monitor', label: 'Monitor' },
  { name: 'keyboard', label: 'Keyboard' },
  { name: 'mouse', label: 'Mouse' },
  { name: 'speaker', label: 'Speaker' },
  { name: 'controller', label: 'Controller' },
  { name: 'television', label: 'TV' },
  { name: 'cellphone', label: 'Phone' },
  { name: 'laptop', label: 'Laptop' },
  { name: 'cup', label: 'Cup' },
  { name: 'bottle-soda', label: 'Bottle' },
  { name: 'tshirt-crew', label: 'Shirt' },
  { name: 'fire', label: 'Fire' },
  { name: 'star', label: 'Star' },
  { name: 'trophy', label: 'Trophy' },
  { name: 'rocket', label: 'Rocket' },
  { name: 'tag', label: 'Tag' },
  { name: 'cart', label: 'Cart' },
  { name: 'web', label: 'Web' },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AdvertiserDashboardScreen({ navigation, route }) {
  const { showError } = useError();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Advertiser data
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [creatives, setCreatives] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  // Admin-specific
  const [adminStats, setAdminStats] = useState(null);

  // UI state
  const [activeTab, setActiveTab] = useState('overview');
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showCreativeModal, setShowCreativeModal] = useState(false);
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Campaign form
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    placements: [],
    budgetCents: '',
    startDate: '',
    endDate: '',
    notes: '',
  });

  // Creative form
  const [creativeForm, setCreativeForm] = useState({
    placement: '',
    name: '',
    tagline: '',
    headline: '',
    description: '',
    cta: '',
    url: '',
    color: '#FCD000',
    logoIcon: 'tag',
  });

  // ============================================================================
  // AUTH & DATA LOADING
  // ============================================================================

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        // Check admin role from Firestore
        try {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'users', u.uid));
          if (userDoc.exists() && userDoc.data().role === 'admin') {
            setIsAdmin(true);
          }
        } catch (e) {
          console.log('[AdvertiserDashboard] Could not check admin role:', e.message);
        }
        await loadAdvertiserData(u.uid);
      } else {
        setLoading(false);
      }
    });
    return unsubAuth;
  }, []);

  const loadAdvertiserData = async (uid) => {
    try {
      const functions = getFunctions();

      // First try the standard advertiser accounts endpoint
      let accts = [];
      try {
        const getAccounts = httpsCallable(functions, 'getMyAdvertiserAccounts');
        const result = await getAccounts();
        accts = result.data?.accounts || [];
      } catch (e) {
        console.log('[AdvertiserDashboard] getMyAdvertiserAccounts failed:', e.message);
      }

      // Admin fallback: if no accounts found, try reading advertisers directly from Firestore
      if (accts.length === 0) {
        try {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'users', uid));
          if (userDoc.exists() && userDoc.data().role === 'admin') {
            setIsAdmin(true);
            // Admin can read all advertisers via Firestore rules (isAdmin() check)
            const advertisersSnap = await getDocs(
              query(collection(db, 'advertisers'), limit(20))
            );
            accts = advertisersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          }
        } catch (e) {
          console.log('[AdvertiserDashboard] Admin fallback failed:', e.message);
        }
      }

      setAccounts(accts);

      if (accts.length > 0) {
        const first = accts[0];
        setSelectedAccount(first);
        await loadCampaignsForAccount(first.id);
        await loadAnalyticsForAccount(first.id);
      }

      // Load admin stats if admin
      try {
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists() && userDoc.data().role === 'admin') {
          await loadAdminStats();
        }
      } catch (e) { /* not admin, skip */ }

    } catch (error) {
      console.error('[AdvertiserDashboard] Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignsForAccount = async (advertiserId) => {
    try {
      // Try cloud function first
      const functions = getFunctions();
      const getCampaigns = httpsCallable(functions, 'getCampaigns');
      const result = await getCampaigns({ advertiserId });
      setCampaigns(result.data?.campaigns || []);
    } catch (error) {
      console.log('[AdvertiserDashboard] getCampaigns CF failed, trying Firestore:', error.message);
      // Fallback: read directly from Firestore (works for admins)
      try {
        const db = getFirestore();
        const campaignsSnap = await getDocs(
          query(
            collection(db, 'ad_campaigns'),
            where('advertiserId', '==', advertiserId)
          )
        );
        setCampaigns(campaignsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e2) {
        console.error('[AdvertiserDashboard] Firestore fallback also failed:', e2.message);
      }
    }
  };

  const loadCreativesForCampaign = async (campaignId) => {
    try {
      const functions = getFunctions();
      const getCreatives = httpsCallable(functions, 'getCreatives');
      const result = await getCreatives({ campaignId });
      setCreatives(result.data?.creatives || []);
    } catch (error) {
      console.log('[AdvertiserDashboard] getCreatives CF failed, trying Firestore:', error.message);
      try {
        const db = getFirestore();
        const creativesSnap = await getDocs(
          query(
            collection(db, 'ad_creatives'),
            where('campaignId', '==', campaignId)
          )
        );
        setCreatives(creativesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e2) {
        console.error('[AdvertiserDashboard] Creatives fallback failed:', e2.message);
      }
    }
  };

  const loadAnalyticsForAccount = async (advertiserId) => {
    try {
      const functions = getFunctions();
      const getAnalytics = httpsCallable(functions, 'getCampaignAnalytics');
      const result = await getAnalytics({ advertiserId });
      setAnalytics(result.data || null);
    } catch (error) {
      console.log('[AdvertiserDashboard] Analytics CF failed, trying Firestore fallback:', error.message);
      // Firestore fallback: read ad_daily_stats directly (works for admins)
      try {
        const db = getFirestore();
        const statsSnap = await getDocs(
          query(
            collection(db, 'ad_daily_stats'),
            where('advertiserId', '==', advertiserId),
            orderBy('date', 'desc'),
            limit(31)
          )
        );
        let daily = statsSnap.docs.map(d => d.data());
        // Sort ascending by date for chart display
        daily.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

        if (daily.length > 0) {
          const totalImpressions = daily.reduce((s, d) => s + (d.impressions || 0), 0);
          const totalClicks = daily.reduce((s, d) => s + (d.clicks || 0), 0);
          const totalSpent = daily.reduce((s, d) => s + (d.spentCents || 0), 0);
          const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100) : 0;

          setAnalytics({
            daily,
            totals: {
              impressions: totalImpressions,
              clicks: totalClicks,
              ctr: parseFloat(ctr.toFixed(2)),
              spentCents: totalSpent,
            },
          });
        } else {
          // Try without orderBy in case composite index is missing
          const rawSnap = await getDocs(
            query(
              collection(db, 'ad_daily_stats'),
              where('advertiserId', '==', advertiserId)
            )
          );
          let rawDaily = rawSnap.docs.map(d => d.data());
          rawDaily.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
          rawDaily = rawDaily.slice(-31);

          if (rawDaily.length > 0) {
            const totalImpressions = rawDaily.reduce((s, d) => s + (d.impressions || 0), 0);
            const totalClicks = rawDaily.reduce((s, d) => s + (d.clicks || 0), 0);
            const totalSpent = rawDaily.reduce((s, d) => s + (d.spentCents || 0), 0);
            const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100) : 0;

            setAnalytics({
              daily: rawDaily,
              totals: {
                impressions: totalImpressions,
                clicks: totalClicks,
                ctr: parseFloat(ctr.toFixed(2)),
                spentCents: totalSpent,
              },
            });
          } else {
            setAnalytics(null);
          }
        }
      } catch (fsErr) {
        console.log('[AdvertiserDashboard] Firestore analytics fallback failed:', fsErr.message);
        setAnalytics(null);
      }
    }
  };

  const loadAdminStats = async () => {
    try {
      const functions = getFunctions();
      const getStats = httpsCallable(functions, 'adminGetAdvertiserStats');
      const result = await getStats();
      setAdminStats(result.data || null);
    } catch (error) {
      console.log('[AdvertiserDashboard] Admin stats CF failed, computing from Firestore:', error.message);
      // Firestore fallback: compute stats from advertisers + campaigns
      try {
        const db = getFirestore();
        const [advertiserSnap, campaignSnap] = await Promise.all([
          getDocs(collection(db, 'advertisers')),
          getDocs(collection(db, 'ad_campaigns')),
        ]);
        const allAdvertisers = advertiserSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const allCampaigns = campaignSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const activeAdvertisers = allAdvertisers.filter(a => a.status === 'active').length;
        const activeCampaigns = allCampaigns.filter(c => c.status === 'active').length;
        const pendingReview = allCampaigns.filter(c => c.status === 'pending_review').length;
        const totalSpent = allCampaigns.reduce((s, c) => s + (c.spentCents || 0), 0);
        const totalBudget = allCampaigns.reduce((s, c) => s + (c.budgetCents || 0), 0);
        const totalImpressions = allCampaigns.reduce((s, c) => s + (c.totalImpressions || 0), 0);
        const totalClicks = allCampaigns.reduce((s, c) => s + (c.totalClicks || 0), 0);

        setAdminStats({
          totalAdvertisers: allAdvertisers.length,
          activeAdvertisers,
          totalCampaigns: allCampaigns.length,
          activeCampaigns,
          pendingReview,
          totalSpentCents: totalSpent,
          totalBudgetCents: totalBudget,
          totalImpressions,
          totalClicks,
        });
      } catch (fsErr) {
        console.log('[AdvertiserDashboard] Firestore admin stats fallback failed:', fsErr.message);
      }
    }
  };

  const onRefresh = useCallback(async () => {
    if (!selectedAccount) return;
    setRefreshing(true);
    await Promise.all([
      loadCampaignsForAccount(selectedAccount.id),
      loadAnalyticsForAccount(selectedAccount.id),
    ]);
    if (isAdmin) await loadAdminStats();
    setRefreshing(false);
  }, [selectedAccount, isAdmin]);

  const switchAccount = async (account) => {
    setSelectedAccount(account);
    setSelectedCampaign(null);
    setCreatives([]);
    setShowAccountSwitcher(false);
    setActiveTab('overview');
    await loadCampaignsForAccount(account.id);
    await loadAnalyticsForAccount(account.id);
  };

  // ============================================================================
  // CAMPAIGN ACTIONS
  // ============================================================================

  const handleCreateCampaign = async () => {
    if (!campaignForm.name || campaignForm.placements.length === 0) {
      showError('Missing Info', 'Campaign name and at least one placement are required.');
      return;
    }

    setSubmitting(true);
    try {
      const functions = getFunctions();
      const createCampaign = httpsCallable(functions, 'createCampaign');
      await createCampaign({
        advertiserId: selectedAccount.id,
        name: campaignForm.name,
        placements: campaignForm.placements,
        budgetCents: parseInt(campaignForm.budgetCents) || 0,
        startDate: campaignForm.startDate || null,
        endDate: campaignForm.endDate || null,
        notes: campaignForm.notes,
      });

      setShowCampaignModal(false);
      setCampaignForm({ name: '', placements: [], budgetCents: '', startDate: '', endDate: '', notes: '' });
      await loadCampaignsForAccount(selectedAccount.id);
      showError('Campaign Created', '"' + campaignForm.name + '" is ready. Add creatives, then submit for review.');
    } catch (error) {
      showError('Error', error.message || 'Failed to create campaign.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitForReview = async (campaignId) => {
    setSubmitting(true);
    try {
      const functions = getFunctions();
      const submitCampaign = httpsCallable(functions, 'submitCampaignForReview');
      await submitCampaign({ campaignId });
      await loadCampaignsForAccount(selectedAccount.id);
      showError('Submitted', 'Campaign submitted for review. Our team will review within 1-2 business days.');
    } catch (error) {
      showError('Error', error.message || 'Failed to submit campaign.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleCampaign = async (campaignId, currentStatus) => {
    const action = currentStatus === 'active' ? 'pause' : 'resume';
    setSubmitting(true);
    try {
      const functions = getFunctions();
      const toggleCampaign = httpsCallable(functions, 'toggleCampaignStatus');
      await toggleCampaign({ campaignId, action });
      await loadCampaignsForAccount(selectedAccount.id);
    } catch (error) {
      showError('Error', error.message || 'Failed to ' + action + ' campaign.');
    } finally {
      setSubmitting(false);
    }
  };

  // Admin review actions
  const handleAdminReviewCampaign = async (campaignId, action, reason) => {
    setSubmitting(true);
    try {
      const functions = getFunctions();
      const reviewCampaign = httpsCallable(functions, 'adminReviewCampaign');
      await reviewCampaign({ campaignId, action, reason });
      await loadCampaignsForAccount(selectedAccount.id);
      showError('Review Complete', 'Campaign ' + action + 'd successfully.');
    } catch (error) {
      showError('Error', error.message || 'Failed to review campaign.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdminReviewCreative = async (creativeId, action, reason) => {
    setSubmitting(true);
    try {
      const functions = getFunctions();
      const reviewCreative = httpsCallable(functions, 'adminReviewCreative');
      await reviewCreative({ creativeId, action, reason });
      if (selectedCampaign) await loadCreativesForCampaign(selectedCampaign.id);
      showError('Review Complete', 'Creative ' + action + 'd successfully.');
    } catch (error) {
      showError('Error', error.message || 'Failed to review creative.');
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================================
  // CREATIVE ACTIONS
  // ============================================================================

  const handleCreateCreative = async () => {
    if (!creativeForm.placement || !creativeForm.name || !creativeForm.headline) {
      showError('Missing Info', 'Placement, brand name, and headline are required.');
      return;
    }
    if (!selectedCampaign) {
      showError('No Campaign', 'Select a campaign first.');
      return;
    }

    setSubmitting(true);
    try {
      const functions = getFunctions();
      const createCreative = httpsCallable(functions, 'createCreative');
      await createCreative({
        campaignId: selectedCampaign.id,
        placement: creativeForm.placement,
        creative: {
          name: creativeForm.name,
          tagline: creativeForm.tagline,
          headline: creativeForm.headline,
          description: creativeForm.description,
          cta: creativeForm.cta || 'Learn More',
          url: creativeForm.url,
          color: creativeForm.color,
          logoIcon: creativeForm.logoIcon,
        },
      });

      setShowCreativeModal(false);
      setCreativeForm({
        placement: '', name: '', tagline: '', headline: '',
        description: '', cta: '', url: '', color: '#FCD000', logoIcon: 'tag',
      });
      await loadCreativesForCampaign(selectedCampaign.id);
      showError('Creative Added', 'Ad creative submitted for review.');
    } catch (error) {
      showError('Error', error.message || 'Failed to create creative.');
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const usd = (cents) => {
    if (!cents && cents !== 0) return '$0';
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const formatNumber = (n) => {
    if (!n && n !== 0) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toLocaleString();
  };

  const formatPercent = (n) => {
    if (!n && n !== 0) return '0.00%';
    return n.toFixed(2) + '%';
  };

  // Compute analytics totals from campaigns if analytics endpoint didn't return data
  const getAnalyticsTotals = () => {
    if (analytics?.totals) return analytics.totals;
    // Fallback: compute from campaign data
    const totalImpressions = campaigns.reduce((sum, c) => sum + (c.totalImpressions || 0), 0);
    const totalClicks = campaigns.reduce((sum, c) => sum + (c.totalClicks || 0), 0);
    const totalSpend = campaigns.reduce((sum, c) => sum + (c.spentCents || 0), 0);
    const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';
    return { impressions: totalImpressions, clicks: totalClicks, ctr: parseFloat(ctr), spentCents: totalSpend };
  };

  const tabs = isAdmin
    ? ['overview', 'campaigns', 'creatives', 'analytics']
    : ['overview', 'campaigns', 'creatives', 'analytics'];

  // ============================================================================
  // LOADING / NO AUTH / NO ACCOUNT STATES
  // ============================================================================

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.centered}>
          <ActivityIndicator size="large" color="#FCD000" />
          <Text style={{ color: '#888', marginTop: 12, fontSize: 13 }}>Loading advertiser data...</Text>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  if (!user) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.centered}>
          <Ionicons name="lock-closed" size={48} color="#333" />
          <Text style={styles.emptyTitle}>Sign In Required</Text>
          <Text style={styles.emptyDesc}>
            Log in to access the Advertiser Dashboard.
          </Text>
          <TouchableOpacity style={styles.primaryBtn} onPress={() => navigation.navigate('Login')}>
            <Text style={styles.primaryBtnText}>Sign In</Text>
          </TouchableOpacity>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  if (accounts.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.centered}>
          <Ionicons name="megaphone-outline" size={48} color="#333" />
          <Text style={styles.emptyTitle}>No Advertiser Account</Text>
          <Text style={styles.emptyDesc}>
            You don't have an advertiser account yet. Submit an inquiry to get started.
          </Text>
          <TouchableOpacity style={styles.primaryBtn} onPress={() => navigation.navigate('Advertiser')}>
            <Text style={styles.primaryBtnText}>Advertiser Info</Text>
          </TouchableOpacity>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  // ============================================================================
  // MAIN DASHBOARD
  // ============================================================================

  const totals = getAnalyticsTotals();

  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#FCD000" />}
      >
        {/* Dashboard Header */}
        <View style={styles.dashHeader}>
          <TouchableOpacity
            style={{ flex: 1 }}
            onPress={() => accounts.length > 1 ? setShowAccountSwitcher(true) : null}
            activeOpacity={accounts.length > 1 ? 0.6 : 1}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Text style={styles.dashTitle}>{selectedAccount?.companyName || 'Dashboard'}</Text>
              {accounts.length > 1 && (
                <Ionicons name="chevron-down" size={18} color="#888" style={{ marginLeft: 6 }} />
              )}
            </View>
            <Text style={styles.dashSubtitle}>
              {isAdmin ? 'Admin View' : 'Advertiser Portal'}
              {selectedAccount?.tier ? ' - ' + selectedAccount.tier.charAt(0).toUpperCase() + selectedAccount.tier.slice(1) + ' Tier' : ''}
            </Text>
          </TouchableOpacity>
          <View style={[styles.statusBadge, { backgroundColor: selectedAccount?.status === 'active' ? '#2ECC7130' : '#E74C3C30' }]}>
            <View style={[styles.statusDot, { backgroundColor: selectedAccount?.status === 'active' ? '#2ECC71' : '#E74C3C' }]} />
            <Text style={[styles.statusText, { color: selectedAccount?.status === 'active' ? '#2ECC71' : '#E74C3C' }]}>
              {selectedAccount?.status === 'active' ? 'Active' : selectedAccount?.status || 'Inactive'}
            </Text>
          </View>
        </View>

        {/* Admin Badge */}
        {isAdmin && (
          <View style={styles.adminBadge}>
            <Ionicons name="shield-checkmark" size={14} color="#FCD000" />
            <Text style={styles.adminBadgeText}>Admin Access - You can review campaigns and creatives</Text>
          </View>
        )}

        {/* KPI Cards */}
        <View style={styles.kpiRow}>
          <View style={styles.kpiCard}>
            <Ionicons name="eye-outline" size={20} color="#3498DB" />
            <Text style={styles.kpiValue}>{formatNumber(totals.impressions || 0)}</Text>
            <Text style={styles.kpiLabel}>Impressions</Text>
          </View>
          <View style={styles.kpiCard}>
            <Ionicons name="finger-print-outline" size={20} color="#2ECC71" />
            <Text style={styles.kpiValue}>{formatNumber(totals.clicks || 0)}</Text>
            <Text style={styles.kpiLabel}>Clicks</Text>
          </View>
          <View style={styles.kpiCard}>
            <Ionicons name="analytics-outline" size={20} color="#FCD000" />
            <Text style={styles.kpiValue}>{formatPercent(totals.ctr || 0)}</Text>
            <Text style={styles.kpiLabel}>CTR</Text>
          </View>
          <View style={styles.kpiCard}>
            <Ionicons name="layers-outline" size={20} color="#9B59B6" />
            <Text style={styles.kpiValue}>{campaigns.filter(c => c.status === 'active').length}</Text>
            <Text style={styles.kpiLabel}>Active</Text>
          </View>
        </View>

        {/* Tab Bar */}
        <View style={styles.tabBar}>
          {tabs.map(tab => (
            <TouchableOpacity
              key={tab}
              style={[styles.tab, activeTab === tab && styles.tabActive]}
              onPress={() => setActiveTab(tab)}
            >
              <Text style={[styles.tabText, activeTab === tab && styles.tabTextActive]}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Tab Content */}
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'campaigns' && renderCampaigns()}
        {activeTab === 'creatives' && renderCreatives()}
        {activeTab === 'analytics' && renderAnalytics()}

        {/* Compliance footer */}
        <View style={styles.complianceFooter}>
          <Ionicons name="information-circle" size={14} color="#444" />
          <Text style={styles.complianceText}>
            Advertising placements are separate from creator compensation.
            Advertisers purchase ad space only; they never influence outcomes.
          </Text>
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>

      {/* Account Switcher Modal */}
      {renderAccountSwitcher()}

      {/* Campaign Creation Modal */}
      {renderCampaignModal()}

      {/* Creative Creation Modal */}
      {renderCreativeModal()}

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );

  // ============================================================================
  // TAB: OVERVIEW
  // ============================================================================

  function renderOverview() {
    const activeCampaigns = campaigns.filter(c => c.status === 'active');
    const pendingCampaigns = campaigns.filter(c => c.status === 'pending_review');
    const totalBudget = campaigns.reduce((sum, c) => sum + (c.budgetCents || 0), 0);
    const totalSpent = campaigns.reduce((sum, c) => sum + (c.spentCents || 0), 0);

    return (
      <View style={styles.tabContent}>
        {/* Account Info */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionLabel}>Account Details</Text>
          <View style={styles.detailRow}>
            <Text style={styles.detailKey}>Company</Text>
            <Text style={styles.detailVal}>{selectedAccount?.companyName || '-'}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailKey}>Contact</Text>
            <Text style={styles.detailVal}>{selectedAccount?.contactName || '-'}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailKey}>Email</Text>
            <Text style={styles.detailVal}>{selectedAccount?.contactEmail || '-'}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailKey}>Tier</Text>
            <Text style={[styles.detailVal, { color: '#FCD000' }]}>
              {(selectedAccount?.tier || 'pilot').charAt(0).toUpperCase() + (selectedAccount?.tier || 'pilot').slice(1)}
            </Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailKey}>Category</Text>
            <Text style={styles.detailVal}>
              {(selectedAccount?.category || 'other').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Text>
          </View>
          {totalBudget > 0 && (
            <View style={[styles.detailRow, { borderBottomWidth: 0 }]}>
              <Text style={styles.detailKey}>Total Budget</Text>
              <Text style={styles.detailVal}>{usd(totalBudget)}</Text>
            </View>
          )}
        </View>

        {/* Performance Summary */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionLabel}>Performance Summary</Text>
          <View style={styles.perfGrid}>
            <View style={styles.perfItem}>
              <Text style={styles.perfValue}>{formatNumber(totals.impressions || 0)}</Text>
              <Text style={styles.perfLabel}>Total Impressions</Text>
            </View>
            <View style={styles.perfItem}>
              <Text style={styles.perfValue}>{formatNumber(totals.clicks || 0)}</Text>
              <Text style={styles.perfLabel}>Total Clicks</Text>
            </View>
            <View style={styles.perfItem}>
              <Text style={styles.perfValue}>{formatPercent(totals.ctr || 0)}</Text>
              <Text style={styles.perfLabel}>Avg. CTR</Text>
            </View>
            <View style={styles.perfItem}>
              <Text style={styles.perfValue}>{campaigns.length}</Text>
              <Text style={styles.perfLabel}>Campaigns</Text>
            </View>
          </View>
        </View>

        {/* Quick Actions */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionLabel}>Quick Actions</Text>
          <TouchableOpacity style={styles.actionRow} onPress={() => { setActiveTab('campaigns'); setShowCampaignModal(true); }}>
            <Ionicons name="add-circle-outline" size={20} color="#FCD000" />
            <Text style={styles.actionText}>Create New Campaign</Text>
            <Ionicons name="chevron-forward" size={16} color="#444" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionRow} onPress={() => setActiveTab('campaigns')}>
            <Ionicons name="list-outline" size={20} color="#3498DB" />
            <Text style={styles.actionText}>View All Campaigns ({campaigns.length})</Text>
            <Ionicons name="chevron-forward" size={16} color="#444" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionRow} onPress={() => setActiveTab('analytics')}>
            <Ionicons name="bar-chart-outline" size={20} color="#2ECC71" />
            <Text style={styles.actionText}>View Analytics</Text>
            <Ionicons name="chevron-forward" size={16} color="#444" />
          </TouchableOpacity>
          <TouchableOpacity style={[styles.actionRow, { borderBottomWidth: 0 }]} onPress={() => navigation.navigate('Advertiser')}>
            <Ionicons name="information-circle-outline" size={20} color="#888" />
            <Text style={styles.actionText}>Placement Guide</Text>
            <Ionicons name="chevron-forward" size={16} color="#444" />
          </TouchableOpacity>
        </View>

        {/* Campaign Summary */}
        {campaigns.length > 0 && (
          <View style={styles.sectionCard}>
            <Text style={styles.sectionLabel}>Recent Campaigns</Text>
            {campaigns.slice(0, 5).map(campaign => (
              <TouchableOpacity
                key={campaign.id}
                style={styles.campaignRow}
                onPress={() => {
                  setSelectedCampaign(campaign);
                  setActiveTab('creatives');
                  loadCreativesForCampaign(campaign.id);
                }}
              >
                <View style={{ flex: 1 }}>
                  <Text style={styles.campaignRowName}>{campaign.name}</Text>
                  <Text style={styles.campaignRowMeta}>
                    {campaign.placements?.map(p => PLACEMENT_LABELS[p] || p).join(', ')}
                  </Text>
                </View>
                <View style={[styles.statusPill, { backgroundColor: (CAMPAIGN_STATUS_COLORS[campaign.status] || '#888') + '20' }]}>
                  <Text style={[styles.statusPillText, { color: CAMPAIGN_STATUS_COLORS[campaign.status] || '#888' }]}>
                    {CAMPAIGN_STATUS_LABELS[campaign.status] || campaign.status}
                  </Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Admin: Pending Reviews */}
        {isAdmin && campaigns.filter(c => c.status === 'pending_review').length > 0 && (
          <View style={[styles.sectionCard, { borderColor: '#F39C12', borderWidth: 1 }]}>
            <Text style={[styles.sectionLabel, { color: '#F39C12' }]}>
              Pending Reviews ({pendingCampaigns.length})
            </Text>
            {pendingCampaigns.map(campaign => (
              <View key={campaign.id} style={styles.campaignRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.campaignRowName}>{campaign.name}</Text>
                  <Text style={styles.campaignRowMeta}>Awaiting admin review</Text>
                </View>
                <View style={{ flexDirection: 'row', gap: 6 }}>
                  <TouchableOpacity
                    style={[styles.miniBtn, { backgroundColor: '#2ECC7130' }]}
                    onPress={() => handleAdminReviewCampaign(campaign.id, 'approve')}
                    disabled={submitting}
                  >
                    <Ionicons name="checkmark" size={16} color="#2ECC71" />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.miniBtn, { backgroundColor: '#E74C3C30' }]}
                    onPress={() => handleAdminReviewCampaign(campaign.id, 'reject', 'Does not meet guidelines.')}
                    disabled={submitting}
                  >
                    <Ionicons name="close" size={16} color="#E74C3C" />
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  }

  // ============================================================================
  // TAB: CAMPAIGNS
  // ============================================================================

  function renderCampaigns() {
    return (
      <View style={styles.tabContent}>
        {/* New Campaign Button */}
        <TouchableOpacity style={styles.newCampaignBtn} onPress={() => setShowCampaignModal(true)}>
          <Ionicons name="add" size={20} color="#000" />
          <Text style={styles.newCampaignBtnText}>New Campaign</Text>
        </TouchableOpacity>

        {campaigns.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="megaphone-outline" size={36} color="#333" />
            <Text style={styles.emptyTitle}>No Campaigns Yet</Text>
            <Text style={styles.emptyDesc}>Create your first campaign to start advertising on CritCash.</Text>
          </View>
        ) : (
          campaigns.map(campaign => (
            <View key={campaign.id} style={styles.campaignCard}>
              {/* Campaign Header */}
              <View style={styles.campaignCardHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.campaignCardName}>{campaign.name}</Text>
                  <Text style={styles.campaignCardMeta}>
                    {campaign.placements?.map(p => PLACEMENT_LABELS[p] || p).join(' + ')}
                  </Text>
                </View>
                <View style={[styles.statusPill, { backgroundColor: (CAMPAIGN_STATUS_COLORS[campaign.status] || '#888') + '20' }]}>
                  <Text style={[styles.statusPillText, { color: CAMPAIGN_STATUS_COLORS[campaign.status] || '#888' }]}>
                    {CAMPAIGN_STATUS_LABELS[campaign.status] || campaign.status}
                  </Text>
                </View>
              </View>

              {/* Stats Row */}
              <View style={styles.campaignStatsRow}>
                <View style={styles.campaignStat}>
                  <Text style={styles.campaignStatValue}>{formatNumber(campaign.totalImpressions || 0)}</Text>
                  <Text style={styles.campaignStatLabel}>Impressions</Text>
                </View>
                <View style={styles.campaignStat}>
                  <Text style={styles.campaignStatValue}>{formatNumber(campaign.totalClicks || 0)}</Text>
                  <Text style={styles.campaignStatLabel}>Clicks</Text>
                </View>
                <View style={styles.campaignStat}>
                  <Text style={styles.campaignStatValue}>
                    {campaign.totalImpressions > 0
                      ? formatPercent((campaign.totalClicks / campaign.totalImpressions) * 100)
                      : '0.00%'}
                  </Text>
                  <Text style={styles.campaignStatLabel}>CTR</Text>
                </View>
                {campaign.budgetCents > 0 && (
                  <View style={styles.campaignStat}>
                    <Text style={styles.campaignStatValue}>{usd(campaign.budgetCents)}</Text>
                    <Text style={styles.campaignStatLabel}>Budget</Text>
                  </View>
                )}
              </View>

              {/* Notes */}
              {campaign.notes ? (
                <Text style={styles.campaignNotes} numberOfLines={2}>{campaign.notes}</Text>
              ) : null}

              {/* Rejection reason */}
              {campaign.status === 'rejected' && campaign.rejectionReason ? (
                <View style={styles.rejectionBox}>
                  <Ionicons name="alert-circle" size={14} color="#E74C3C" />
                  <Text style={styles.rejectionText}>{campaign.rejectionReason}</Text>
                </View>
              ) : null}

              {/* Action Buttons */}
              <View style={styles.campaignActions}>
                <TouchableOpacity
                  style={styles.campaignActionBtn}
                  onPress={() => {
                    setSelectedCampaign(campaign);
                    setActiveTab('creatives');
                    loadCreativesForCampaign(campaign.id);
                  }}
                >
                  <Ionicons name="images-outline" size={16} color="#FCD000" />
                  <Text style={styles.campaignActionText}>Creatives</Text>
                </TouchableOpacity>

                {campaign.status === 'draft' && (
                  <TouchableOpacity
                    style={[styles.campaignActionBtn, styles.campaignActionSubmit]}
                    onPress={() => handleSubmitForReview(campaign.id)}
                    disabled={submitting}
                  >
                    <Ionicons name="paper-plane-outline" size={16} color="#000" />
                    <Text style={[styles.campaignActionText, { color: '#000' }]}>Submit for Review</Text>
                  </TouchableOpacity>
                )}

                {campaign.status === 'rejected' && (
                  <TouchableOpacity
                    style={[styles.campaignActionBtn, styles.campaignActionSubmit]}
                    onPress={() => handleSubmitForReview(campaign.id)}
                    disabled={submitting}
                  >
                    <Ionicons name="refresh-outline" size={16} color="#000" />
                    <Text style={[styles.campaignActionText, { color: '#000' }]}>Resubmit</Text>
                  </TouchableOpacity>
                )}

                {(campaign.status === 'active' || campaign.status === 'paused') && (
                  <TouchableOpacity
                    style={styles.campaignActionBtn}
                    onPress={() => handleToggleCampaign(campaign.id, campaign.status)}
                    disabled={submitting}
                  >
                    <Ionicons
                      name={campaign.status === 'active' ? 'pause-outline' : 'play-outline'}
                      size={16}
                      color={campaign.status === 'active' ? '#E67E22' : '#2ECC71'}
                    />
                    <Text style={styles.campaignActionText}>
                      {campaign.status === 'active' ? 'Pause' : 'Resume'}
                    </Text>
                  </TouchableOpacity>
                )}

                {/* Admin: Review button for pending campaigns */}
                {isAdmin && campaign.status === 'pending_review' && (
                  <>
                    <TouchableOpacity
                      style={[styles.campaignActionBtn, { backgroundColor: '#2ECC7130' }]}
                      onPress={() => handleAdminReviewCampaign(campaign.id, 'approve')}
                      disabled={submitting}
                    >
                      <Ionicons name="checkmark-circle-outline" size={16} color="#2ECC71" />
                      <Text style={[styles.campaignActionText, { color: '#2ECC71' }]}>Approve</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.campaignActionBtn, { backgroundColor: '#E74C3C30' }]}
                      onPress={() => handleAdminReviewCampaign(campaign.id, 'reject', 'Does not meet ad guidelines.')}
                      disabled={submitting}
                    >
                      <Ionicons name="close-circle-outline" size={16} color="#E74C3C" />
                      <Text style={[styles.campaignActionText, { color: '#E74C3C' }]}>Reject</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </View>
          ))
        )}
      </View>
    );
  }

  // ============================================================================
  // TAB: CREATIVES
  // ============================================================================

  function renderCreatives() {
    return (
      <View style={styles.tabContent}>
        {/* Campaign selector */}
        {campaigns.length > 0 && (
          <View style={styles.campaignSelector}>
            <Text style={styles.campaignSelectorLabel}>Campaign:</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flex: 1 }}>
              {campaigns.map(c => (
                <TouchableOpacity
                  key={c.id}
                  style={[styles.campaignChip, selectedCampaign?.id === c.id && styles.campaignChipActive]}
                  onPress={() => {
                    setSelectedCampaign(c);
                    loadCreativesForCampaign(c.id);
                  }}
                >
                  <Text style={[styles.campaignChipText, selectedCampaign?.id === c.id && styles.campaignChipTextActive]}>
                    {c.name}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}

        {!selectedCampaign ? (
          <View style={styles.emptyState}>
            <Ionicons name="images-outline" size={36} color="#333" />
            <Text style={styles.emptyTitle}>Select a Campaign</Text>
            <Text style={styles.emptyDesc}>Choose a campaign above to manage its ad creatives.</Text>
          </View>
        ) : (
          <>
            {/* New Creative Button */}
            <TouchableOpacity style={styles.newCampaignBtn} onPress={() => setShowCreativeModal(true)}>
              <Ionicons name="add" size={20} color="#000" />
              <Text style={styles.newCampaignBtnText}>New Creative</Text>
            </TouchableOpacity>

            {creatives.length === 0 ? (
              <View style={styles.emptyState}>
                <Ionicons name="color-palette-outline" size={36} color="#333" />
                <Text style={styles.emptyTitle}>No Creatives</Text>
                <Text style={styles.emptyDesc}>Add ad creatives for "{selectedCampaign.name}".</Text>
              </View>
            ) : (
              creatives.map(creative => (
                <View key={creative.id} style={styles.creativeCard}>
                  {/* Preview */}
                  <View style={[styles.creativePreview, { borderColor: (creative.creative?.color || '#FFF') + '40' }]}>
                    <View style={[styles.creativePreviewAccent, { backgroundColor: creative.creative?.color || '#FFF' }]} />
                    <View style={styles.creativePreviewContent}>
                      <View style={[styles.creativePreviewLogo, { backgroundColor: (creative.creative?.color || '#FFF') + '20' }]}>
                        <MaterialCommunityIcons
                          name={creative.creative?.logoIcon || 'tag'}
                          size={22}
                          color={creative.creative?.color || '#FFF'}
                        />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.creativePreviewBrand, { color: creative.creative?.color || '#FFF' }]}>
                          {creative.creative?.name || 'Brand'}
                        </Text>
                        <Text style={styles.creativePreviewHeadline}>
                          {creative.creative?.headline || 'Headline'}
                        </Text>
                        <Text style={styles.creativePreviewDesc} numberOfLines={1}>
                          {creative.creative?.description || ''}
                        </Text>
                      </View>
                      <View style={[styles.creativePreviewCta, { backgroundColor: creative.creative?.color || '#FFF' }]}>
                        <Text style={styles.creativePreviewCtaText}>{creative.creative?.cta || 'CTA'}</Text>
                      </View>
                    </View>
                  </View>

                  {/* Meta + Stats */}
                  <View style={styles.creativeMeta}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.creativeMetaPlacement}>{PLACEMENT_LABELS[creative.placement] || creative.placement}</Text>
                      <Text style={styles.creativeMetaStats}>
                        {formatNumber(creative.impressions || 0)} impr. / {formatNumber(creative.clicks || 0)} clicks
                        {(creative.impressions || 0) > 0 && (
                          ' / ' + formatPercent(((creative.clicks || 0) / creative.impressions) * 100) + ' CTR'
                        )}
                      </Text>
                    </View>
                    <View style={[styles.statusPill, { backgroundColor: (CREATIVE_STATUS_COLORS[creative.status] || '#888') + '20' }]}>
                      <Text style={[styles.statusPillText, { color: CREATIVE_STATUS_COLORS[creative.status] || '#888' }]}>
                        {CREATIVE_STATUS_LABELS[creative.status] || creative.status}
                      </Text>
                    </View>
                  </View>

                  {/* Admin: Review buttons for pending creatives */}
                  {isAdmin && creative.status === 'pending' && (
                    <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                      <TouchableOpacity
                        style={[styles.campaignActionBtn, { backgroundColor: '#2ECC7130', flex: 1, justifyContent: 'center' }]}
                        onPress={() => handleAdminReviewCreative(creative.id, 'approve')}
                        disabled={submitting}
                      >
                        <Ionicons name="checkmark-circle-outline" size={16} color="#2ECC71" />
                        <Text style={[styles.campaignActionText, { color: '#2ECC71' }]}>Approve</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.campaignActionBtn, { backgroundColor: '#E74C3C30', flex: 1, justifyContent: 'center' }]}
                        onPress={() => handleAdminReviewCreative(creative.id, 'reject', 'Creative does not meet guidelines.')}
                        disabled={submitting}
                      >
                        <Ionicons name="close-circle-outline" size={16} color="#E74C3C" />
                        <Text style={[styles.campaignActionText, { color: '#E74C3C' }]}>Reject</Text>
                      </TouchableOpacity>
                    </View>
                  )}

                  {/* Rejection reason */}
                  {creative.status === 'rejected' && creative.rejectionReason ? (
                    <View style={[styles.rejectionBox, { marginTop: 8 }]}>
                      <Ionicons name="alert-circle" size={14} color="#E74C3C" />
                      <Text style={styles.rejectionText}>{creative.rejectionReason}</Text>
                    </View>
                  ) : null}
                </View>
              ))
            )}
          </>
        )}
      </View>
    );
  }

  // ============================================================================
  // TAB: ANALYTICS
  // ============================================================================

  function renderAnalytics() {
    const dailyData = analytics?.daily || [];
    const maxImpressions = Math.max(...dailyData.map(d => d.impressions || 0), 1);

    return (
      <View style={styles.tabContent}>
        {/* Summary Cards */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionLabel}>Overall Performance</Text>
          <View style={styles.perfGrid}>
            <View style={styles.perfItem}>
              <Text style={[styles.perfValue, { color: '#3498DB' }]}>{formatNumber(totals.impressions || 0)}</Text>
              <Text style={styles.perfLabel}>Total Impressions</Text>
            </View>
            <View style={styles.perfItem}>
              <Text style={[styles.perfValue, { color: '#2ECC71' }]}>{formatNumber(totals.clicks || 0)}</Text>
              <Text style={styles.perfLabel}>Total Clicks</Text>
            </View>
            <View style={styles.perfItem}>
              <Text style={[styles.perfValue, { color: '#FCD000' }]}>{formatPercent(totals.ctr || 0)}</Text>
              <Text style={styles.perfLabel}>Click-Through Rate</Text>
            </View>
            <View style={styles.perfItem}>
              <Text style={[styles.perfValue, { color: '#9B59B6' }]}>{usd(totals.spentCents || 0)}</Text>
              <Text style={styles.perfLabel}>Total Spend</Text>
            </View>
          </View>
        </View>

        {/* Daily Impressions Chart (simple bar chart) */}
        {dailyData.length > 0 && (
          <View style={styles.sectionCard}>
            <Text style={styles.sectionLabel}>Daily Impressions (Last {dailyData.length} Days)</Text>
            <View style={styles.chartContainer}>
              {dailyData.slice(-14).map((day, idx) => {
                const barHeight = Math.max(((day.impressions || 0) / maxImpressions) * 100, 2);
                return (
                  <View key={idx} style={styles.chartBarWrap}>
                    <Text style={styles.chartBarValue}>{formatNumber(day.impressions || 0)}</Text>
                    <View style={[styles.chartBar, { height: barHeight, backgroundColor: '#3498DB' }]} />
                    <Text style={styles.chartBarLabel}>{day.date?.slice(-5) || ''}</Text>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* Per-Campaign Breakdown */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionLabel}>Campaign Breakdown</Text>
          {campaigns.length === 0 ? (
            <Text style={{ color: '#888', fontSize: 13 }}>No campaigns to display.</Text>
          ) : (
            campaigns.map(campaign => {
              const cImpr = campaign.totalImpressions || 0;
              const cClicks = campaign.totalClicks || 0;
              const cCtr = cImpr > 0 ? ((cClicks / cImpr) * 100) : 0;
              return (
                <View key={campaign.id} style={styles.analyticsRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.analyticsRowName}>{campaign.name}</Text>
                    <View style={styles.analyticsRowStats}>
                      <Text style={styles.analyticsRowStat}>{formatNumber(cImpr)} impr.</Text>
                      <Text style={[styles.analyticsRowStat, { color: '#2ECC71' }]}>{formatNumber(cClicks)} clicks</Text>
                      <Text style={[styles.analyticsRowStat, { color: '#FCD000' }]}>{formatPercent(cCtr)}</Text>
                    </View>
                  </View>
                  <View style={[styles.statusPill, { backgroundColor: (CAMPAIGN_STATUS_COLORS[campaign.status] || '#888') + '20' }]}>
                    <Text style={[styles.statusPillText, { color: CAMPAIGN_STATUS_COLORS[campaign.status] || '#888' }]}>
                      {CAMPAIGN_STATUS_LABELS[campaign.status] || campaign.status}
                    </Text>
                  </View>
                </View>
              );
            })
          )}
        </View>

        {/* Per-Placement Performance */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionLabel}>Performance by Placement</Text>
          {(() => {
            const placementMap = {};
            campaigns.forEach(c => {
              (c.placements || []).forEach(p => {
                if (!placementMap[p]) placementMap[p] = { impressions: 0, clicks: 0 };
                // Distribute campaign totals evenly across placements for display
                const pCount = (c.placements || []).length || 1;
                placementMap[p].impressions += Math.round((c.totalImpressions || 0) / pCount);
                placementMap[p].clicks += Math.round((c.totalClicks || 0) / pCount);
              });
            });
            const entries = Object.entries(placementMap);
            if (entries.length === 0) {
              return <Text style={{ color: '#888', fontSize: 13 }}>No placement data yet.</Text>;
            }
            return entries.map(([placement, data]) => (
              <View key={placement} style={styles.analyticsRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.analyticsRowName}>{PLACEMENT_LABELS[placement] || placement}</Text>
                  <View style={styles.analyticsRowStats}>
                    <Text style={styles.analyticsRowStat}>{formatNumber(data.impressions)} impr.</Text>
                    <Text style={[styles.analyticsRowStat, { color: '#2ECC71' }]}>{formatNumber(data.clicks)} clicks</Text>
                    <Text style={[styles.analyticsRowStat, { color: '#FCD000' }]}>
                      {data.impressions > 0 ? formatPercent((data.clicks / data.impressions) * 100) : '0.00%'}
                    </Text>
                  </View>
                </View>
              </View>
            ));
          })()}
        </View>
      </View>
    );
  }

  // ============================================================================
  // MODALS
  // ============================================================================

  function renderAccountSwitcher() {
    return (
      <Modal visible={showAccountSwitcher} transparent animationType="fade" onRequestClose={() => setShowAccountSwitcher(false)}>
        <View style={styles.modalBackdrop}>
          <View style={[styles.modalContainer, { maxWidth: 400 }]}>
            <ScrollView contentContainerStyle={styles.modalScroll}>
              <View style={styles.modalHeader}>
                <Ionicons name="business" size={28} color="#FCD000" />
                <Text style={styles.modalTitle}>Switch Account</Text>
              </View>
              {accounts.map(acct => (
                <TouchableOpacity
                  key={acct.id}
                  style={[styles.actionRow, selectedAccount?.id === acct.id && { backgroundColor: '#FCD00010' }]}
                  onPress={() => switchAccount(acct)}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={styles.campaignRowName}>{acct.companyName}</Text>
                    <Text style={styles.campaignRowMeta}>{acct.contactEmail}</Text>
                  </View>
                  {selectedAccount?.id === acct.id && (
                    <Ionicons name="checkmark-circle" size={20} color="#FCD000" />
                  )}
                </TouchableOpacity>
              ))}
              <TouchableOpacity
                style={[styles.modalCancelBtn, { marginTop: 16 }]}
                onPress={() => setShowAccountSwitcher(false)}
              >
                <Text style={styles.modalCancelText}>Close</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  }

  function renderCampaignModal() {
    return (
      <Modal visible={showCampaignModal} transparent animationType="fade" onRequestClose={() => setShowCampaignModal(false)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalContainer}>
            <ScrollView contentContainerStyle={styles.modalScroll}>
              <View style={styles.modalHeader}>
                <Ionicons name="megaphone" size={28} color="#FCD000" />
                <Text style={styles.modalTitle}>New Campaign</Text>
              </View>

              {/* Name */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Campaign Name *</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="e.g. Q1 2026 Brand Awareness"
                  placeholderTextColor="#555"
                  value={campaignForm.name}
                  onChangeText={v => setCampaignForm({ ...campaignForm, name: v })}
                />
              </View>

              {/* Placements */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Placements * (select one or more)</Text>
                <View style={styles.placementGrid}>
                  {PLACEMENT_OPTIONS.map(opt => {
                    const selected = campaignForm.placements.includes(opt.value);
                    return (
                      <TouchableOpacity
                        key={opt.value}
                        style={[styles.placementChip, selected && styles.placementChipActive]}
                        onPress={() => {
                          const next = selected
                            ? campaignForm.placements.filter(p => p !== opt.value)
                            : [...campaignForm.placements, opt.value];
                          setCampaignForm({ ...campaignForm, placements: next });
                        }}
                      >
                        {selected && <Ionicons name="checkmark" size={12} color="#000" style={{ marginRight: 4 }} />}
                        <Text style={[styles.placementChipText, selected && styles.placementChipTextActive]}>
                          {opt.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>

              {/* Budget */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Budget (cents, optional)</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="e.g. 100000 ($1,000)"
                  placeholderTextColor="#555"
                  keyboardType="numeric"
                  value={campaignForm.budgetCents}
                  onChangeText={v => setCampaignForm({ ...campaignForm, budgetCents: v })}
                />
              </View>

              {/* Notes */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Notes</Text>
                <TextInput
                  style={[styles.formInput, styles.formTextArea]}
                  placeholder="Campaign goals, target audience, etc."
                  placeholderTextColor="#555"
                  multiline
                  numberOfLines={3}
                  value={campaignForm.notes}
                  onChangeText={v => setCampaignForm({ ...campaignForm, notes: v })}
                />
              </View>

              <View style={styles.modalActions}>
                <TouchableOpacity style={styles.modalCancelBtn} onPress={() => setShowCampaignModal(false)}>
                  <Text style={styles.modalCancelText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.modalSubmitBtn, submitting && { opacity: 0.5 }]}
                  onPress={handleCreateCampaign}
                  disabled={submitting}
                >
                  {submitting ? (
                    <ActivityIndicator size="small" color="#000" />
                  ) : (
                    <Text style={styles.modalSubmitText}>Create Campaign</Text>
                  )}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  }

  function renderCreativeModal() {
    return (
      <Modal visible={showCreativeModal} transparent animationType="fade" onRequestClose={() => setShowCreativeModal(false)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalContainer}>
            <ScrollView contentContainerStyle={styles.modalScroll}>
              <View style={styles.modalHeader}>
                <Ionicons name="color-palette" size={28} color="#FCD000" />
                <Text style={styles.modalTitle}>New Ad Creative</Text>
                {selectedCampaign && (
                  <Text style={styles.modalSubtitle}>for: {selectedCampaign.name}</Text>
                )}
              </View>

              {/* Placement */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Placement Type *</Text>
                <View style={styles.placementGrid}>
                  {(selectedCampaign?.placements || Object.keys(PLACEMENT_LABELS)).map(p => (
                    <TouchableOpacity
                      key={p}
                      style={[styles.placementChip, creativeForm.placement === p && styles.placementChipActive]}
                      onPress={() => setCreativeForm({ ...creativeForm, placement: p })}
                    >
                      {creativeForm.placement === p && <Ionicons name="checkmark" size={12} color="#000" style={{ marginRight: 4 }} />}
                      <Text style={[styles.placementChipText, creativeForm.placement === p && styles.placementChipTextActive]}>
                        {PLACEMENT_LABELS[p] || p}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Brand Name */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Brand Name *</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="Your brand name"
                  placeholderTextColor="#555"
                  value={creativeForm.name}
                  onChangeText={v => setCreativeForm({ ...creativeForm, name: v })}
                />
              </View>

              {/* Tagline */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Tagline</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="Short tagline"
                  placeholderTextColor="#555"
                  value={creativeForm.tagline}
                  onChangeText={v => setCreativeForm({ ...creativeForm, tagline: v })}
                />
              </View>

              {/* Headline */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Headline *</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="Ad headline"
                  placeholderTextColor="#555"
                  value={creativeForm.headline}
                  onChangeText={v => setCreativeForm({ ...creativeForm, headline: v })}
                />
              </View>

              {/* Description */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Description</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="Short description"
                  placeholderTextColor="#555"
                  value={creativeForm.description}
                  onChangeText={v => setCreativeForm({ ...creativeForm, description: v })}
                />
              </View>

              {/* CTA */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Call to Action</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="e.g. Shop Now, Learn More"
                  placeholderTextColor="#555"
                  value={creativeForm.cta}
                  onChangeText={v => setCreativeForm({ ...creativeForm, cta: v })}
                />
              </View>

              {/* URL */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Destination URL</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="https://your-website.com"
                  placeholderTextColor="#555"
                  autoCapitalize="none"
                  value={creativeForm.url}
                  onChangeText={v => setCreativeForm({ ...creativeForm, url: v })}
                />
              </View>

              {/* Brand Color */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Brand Color (hex)</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <TextInput
                    style={[styles.formInput, { flex: 1 }]}
                    placeholder="#FCD000"
                    placeholderTextColor="#555"
                    autoCapitalize="none"
                    value={creativeForm.color}
                    onChangeText={v => setCreativeForm({ ...creativeForm, color: v })}
                  />
                  <View style={[styles.colorPreview, { backgroundColor: creativeForm.color || '#FCD000' }]} />
                </View>
              </View>

              {/* Logo Icon */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Logo Icon</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <View style={styles.iconGrid}>
                    {LOGO_ICON_OPTIONS.map(icon => (
                      <TouchableOpacity
                        key={icon.name}
                        style={[styles.iconChoice, creativeForm.logoIcon === icon.name && styles.iconChoiceActive]}
                        onPress={() => setCreativeForm({ ...creativeForm, logoIcon: icon.name })}
                      >
                        <MaterialCommunityIcons
                          name={icon.name}
                          size={20}
                          color={creativeForm.logoIcon === icon.name ? '#000' : '#888'}
                        />
                      </TouchableOpacity>
                    ))}
                  </View>
                </ScrollView>
              </View>

              {/* Live Preview */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Preview</Text>
                <View style={[styles.creativePreview, { borderColor: (creativeForm.color || '#FFF') + '40' }]}>
                  <View style={[styles.creativePreviewAccent, { backgroundColor: creativeForm.color || '#FFF' }]} />
                  <View style={styles.creativePreviewContent}>
                    <View style={[styles.creativePreviewLogo, { backgroundColor: (creativeForm.color || '#FFF') + '20' }]}>
                      <MaterialCommunityIcons
                        name={creativeForm.logoIcon || 'tag'}
                        size={22}
                        color={creativeForm.color || '#FFF'}
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.creativePreviewBrand, { color: creativeForm.color || '#FFF' }]}>
                        {creativeForm.name || 'Brand Name'}
                      </Text>
                      <Text style={styles.creativePreviewHeadline}>
                        {creativeForm.headline || 'Your Headline'}
                      </Text>
                      <Text style={styles.creativePreviewDesc} numberOfLines={1}>
                        {creativeForm.description || 'Description goes here'}
                      </Text>
                    </View>
                    <View style={[styles.creativePreviewCta, { backgroundColor: creativeForm.color || '#FFF' }]}>
                      <Text style={styles.creativePreviewCtaText}>{creativeForm.cta || 'CTA'}</Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.modalActions}>
                <TouchableOpacity style={styles.modalCancelBtn} onPress={() => setShowCreativeModal(false)}>
                  <Text style={styles.modalCancelText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.modalSubmitBtn, submitting && { opacity: 0.5 }]}
                  onPress={handleCreateCreative}
                  disabled={submitting}
                >
                  {submitting ? (
                    <ActivityIndicator size="small" color="#000" />
                  ) : (
                    <Text style={styles.modalSubmitText}>Submit Creative</Text>
                  )}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  }
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#000' },
  scrollView: { flex: 1 },
  scrollContent: { paddingBottom: 80 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 24 },

  // Dashboard Header
  dashHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    padding: 20, borderBottomWidth: 1, borderBottomColor: '#1A1A1A',
  },
  dashTitle: { fontSize: 22, fontWeight: 'bold', color: '#FFF' },
  dashSubtitle: { fontSize: 13, color: '#888', marginTop: 2 },
  statusBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 10, paddingVertical: 5, borderRadius: 12,
  },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  statusText: { fontSize: 12, fontWeight: '600' },

  // Admin Badge
  adminBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    marginHorizontal: 16, marginTop: 8, paddingVertical: 8, paddingHorizontal: 12,
    backgroundColor: '#FCD00010', borderRadius: 8, borderWidth: 1, borderColor: '#FCD00030',
  },
  adminBadgeText: { fontSize: 12, color: '#FCD000', fontWeight: '500' },

  // KPI Row
  kpiRow: { flexDirection: 'row', padding: 12, gap: 8 },
  kpiCard: {
    flex: 1, backgroundColor: '#111', borderRadius: 10, padding: 12,
    alignItems: 'center', borderWidth: 1, borderColor: '#1A1A1A',
  },
  kpiValue: { fontSize: 18, fontWeight: 'bold', color: '#FFF', marginTop: 6 },
  kpiLabel: { fontSize: 10, color: '#888', marginTop: 2 },

  // Tabs
  tabBar: {
    flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#1A1A1A',
    paddingHorizontal: 16,
  },
  tab: { paddingVertical: 12, paddingHorizontal: 16, marginRight: 4 },
  tabActive: { borderBottomWidth: 2, borderBottomColor: '#FCD000' },
  tabText: { fontSize: 14, color: '#888', fontWeight: '600' },
  tabTextActive: { color: '#FCD000' },
  tabContent: { padding: 16 },

  // Section Cards
  sectionCard: {
    backgroundColor: '#111', borderRadius: 10, padding: 16,
    marginBottom: 12, borderWidth: 1, borderColor: '#1A1A1A',
  },
  sectionLabel: { fontSize: 14, fontWeight: 'bold', color: '#FCD000', marginBottom: 12 },

  // Performance Grid
  perfGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  perfItem: {
    width: '48%', backgroundColor: '#0A0A0A', borderRadius: 8,
    padding: 12, alignItems: 'center',
  },
  perfValue: { fontSize: 20, fontWeight: 'bold', color: '#FFF' },
  perfLabel: { fontSize: 11, color: '#888', marginTop: 4 },

  // Detail Rows
  detailRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#1A1A1A' },
  detailKey: { fontSize: 13, color: '#888' },
  detailVal: { fontSize: 13, color: '#FFF', fontWeight: '500' },

  // Action Rows
  actionRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#1A1A1A',
  },
  actionText: { flex: 1, fontSize: 14, color: '#FFF' },

  // Campaign Rows (overview)
  campaignRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#1A1A1A',
  },
  campaignRowName: { fontSize: 14, color: '#FFF', fontWeight: '600' },
  campaignRowMeta: { fontSize: 11, color: '#888', marginTop: 2 },

  // Status Pill
  statusPill: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  statusPillText: { fontSize: 11, fontWeight: '600' },

  // Mini Button (admin approve/reject)
  miniBtn: {
    width: 32, height: 32, borderRadius: 8,
    justifyContent: 'center', alignItems: 'center',
  },

  // New Campaign Button
  newCampaignBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    backgroundColor: '#FCD000', borderRadius: 8, paddingVertical: 12, marginBottom: 16,
  },
  newCampaignBtnText: { fontSize: 14, fontWeight: 'bold', color: '#000' },

  // Campaign Card
  campaignCard: {
    backgroundColor: '#111', borderRadius: 10, padding: 16,
    marginBottom: 12, borderWidth: 1, borderColor: '#1A1A1A',
  },
  campaignCardHeader: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12 },
  campaignCardName: { fontSize: 16, fontWeight: 'bold', color: '#FFF' },
  campaignCardMeta: { fontSize: 11, color: '#888', marginTop: 2 },
  campaignStatsRow: { flexDirection: 'row', gap: 16, marginBottom: 12, paddingTop: 8, borderTopWidth: 1, borderTopColor: '#1A1A1A' },
  campaignStat: { alignItems: 'center' },
  campaignStatValue: { fontSize: 16, fontWeight: 'bold', color: '#FFF' },
  campaignStatLabel: { fontSize: 10, color: '#888', marginTop: 2 },
  campaignNotes: { fontSize: 12, color: '#888', marginBottom: 10, fontStyle: 'italic' },
  campaignActions: { flexDirection: 'row', gap: 8, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#1A1A1A', flexWrap: 'wrap' },
  campaignActionBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#1A1A1A', paddingVertical: 8, paddingHorizontal: 12, borderRadius: 6,
  },
  campaignActionSubmit: { backgroundColor: '#FCD000' },
  campaignActionText: { fontSize: 12, color: '#FFF', fontWeight: '600' },

  // Rejection
  rejectionBox: {
    flexDirection: 'row', gap: 6, backgroundColor: '#E74C3C15', borderRadius: 6,
    padding: 10, marginBottom: 10,
  },
  rejectionText: { flex: 1, fontSize: 12, color: '#E74C3C' },

  // Campaign Selector
  campaignSelector: {
    flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 16,
  },
  campaignSelectorLabel: { fontSize: 13, color: '#888', fontWeight: '600' },
  campaignChip: {
    backgroundColor: '#1A1A1A', paddingVertical: 6, paddingHorizontal: 12,
    borderRadius: 16, marginRight: 8, borderWidth: 1, borderColor: '#333',
  },
  campaignChipActive: { backgroundColor: '#FCD000', borderColor: '#FCD000' },
  campaignChipText: { fontSize: 12, color: '#CCC' },
  campaignChipTextActive: { color: '#000', fontWeight: '600' },

  // Creative Card
  creativeCard: {
    backgroundColor: '#111', borderRadius: 10, padding: 12,
    marginBottom: 12, borderWidth: 1, borderColor: '#1A1A1A',
  },
  creativePreview: {
    borderRadius: 8, borderWidth: 1, overflow: 'hidden', marginBottom: 10,
    backgroundColor: '#0A0A0A',
  },
  creativePreviewAccent: {
    position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
  },
  creativePreviewContent: {
    flexDirection: 'row', alignItems: 'center', padding: 10, paddingLeft: 12, gap: 10,
  },
  creativePreviewLogo: {
    width: 36, height: 36, borderRadius: 8, justifyContent: 'center', alignItems: 'center',
  },
  creativePreviewBrand: { fontSize: 12, fontWeight: 'bold' },
  creativePreviewHeadline: { fontSize: 13, fontWeight: '600', color: '#FFF', marginTop: 1 },
  creativePreviewDesc: { fontSize: 10, color: '#888' },
  creativePreviewCta: { paddingVertical: 5, paddingHorizontal: 10, borderRadius: 5 },
  creativePreviewCtaText: { fontSize: 10, fontWeight: 'bold', color: '#000' },
  creativeMeta: { flexDirection: 'row', alignItems: 'center' },
  creativeMetaPlacement: { fontSize: 12, color: '#FCD000', fontWeight: '600' },
  creativeMetaStats: { fontSize: 11, color: '#888', marginTop: 2 },

  // Analytics
  chartContainer: {
    flexDirection: 'row', alignItems: 'flex-end', gap: 4, height: 140,
    paddingTop: 20,
  },
  chartBarWrap: { flex: 1, alignItems: 'center', justifyContent: 'flex-end' },
  chartBar: { width: '80%', borderRadius: 3, minHeight: 2 },
  chartBarValue: { fontSize: 8, color: '#888', marginBottom: 2 },
  chartBarLabel: { fontSize: 8, color: '#555', marginTop: 4 },
  analyticsRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#1A1A1A',
  },
  analyticsRowName: { fontSize: 14, color: '#FFF', fontWeight: '600' },
  analyticsRowStats: { flexDirection: 'row', gap: 12, marginTop: 4 },
  analyticsRowStat: { fontSize: 12, color: '#888' },

  // Empty State
  emptyState: { alignItems: 'center', paddingVertical: 40 },
  emptyTitle: { fontSize: 16, fontWeight: 'bold', color: '#FFF', marginTop: 12 },
  emptyDesc: { fontSize: 13, color: '#888', textAlign: 'center', marginTop: 6, maxWidth: 280 },

  // Primary Button
  primaryBtn: { backgroundColor: '#FCD000', paddingVertical: 12, paddingHorizontal: 28, borderRadius: 8, marginTop: 16 },
  primaryBtnText: { color: '#000', fontSize: 15, fontWeight: 'bold' },

  // Compliance Footer
  complianceFooter: {
    flexDirection: 'row', gap: 8, padding: 16, marginTop: 12,
    backgroundColor: '#0A0A0A', borderRadius: 8, marginHorizontal: 16,
  },
  complianceText: { flex: 1, fontSize: 10, color: '#555', lineHeight: 15 },

  // Modals
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.85)', justifyContent: 'center', alignItems: 'center', padding: 16 },
  modalContainer: {
    backgroundColor: '#111', borderRadius: 16, width: '100%', maxWidth: 500, maxHeight: '90%',
  },
  modalScroll: { padding: 20 },
  modalHeader: { alignItems: 'center', marginBottom: 20 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: '#FFF', marginTop: 8 },
  modalSubtitle: { fontSize: 13, color: '#FCD000', marginTop: 4 },

  // Forms
  formGroup: { marginBottom: 14 },
  formLabel: { fontSize: 12, color: '#888', fontWeight: '600', marginBottom: 6 },
  formInput: {
    backgroundColor: '#1A1A1A', borderRadius: 8, padding: 12, fontSize: 14,
    color: '#FFF', borderWidth: 1, borderColor: '#333',
  },
  formTextArea: { height: 80, textAlignVertical: 'top' },

  placementGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  placementChip: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#1A1A1A', paddingVertical: 6, paddingHorizontal: 10,
    borderRadius: 14, borderWidth: 1, borderColor: '#333',
  },
  placementChipActive: { backgroundColor: '#FCD000', borderColor: '#FCD000' },
  placementChipText: { fontSize: 11, color: '#CCC' },
  placementChipTextActive: { color: '#000', fontWeight: '600' },

  colorPreview: { width: 32, height: 32, borderRadius: 6, borderWidth: 1, borderColor: '#333' },

  iconGrid: { flexDirection: 'row', gap: 8 },
  iconChoice: {
    width: 36, height: 36, borderRadius: 8, justifyContent: 'center', alignItems: 'center',
    backgroundColor: '#1A1A1A', borderWidth: 1, borderColor: '#333',
  },
  iconChoiceActive: { backgroundColor: '#FCD000', borderColor: '#FCD000' },

  modalActions: { flexDirection: 'row', gap: 12, marginTop: 20 },
  modalCancelBtn: { flex: 1, backgroundColor: '#1A1A1A', padding: 12, borderRadius: 8, alignItems: 'center' },
  modalCancelText: { color: '#FFF', fontSize: 14, fontWeight: '600' },
  modalSubmitBtn: { flex: 2, backgroundColor: '#FCD000', padding: 12, borderRadius: 8, alignItems: 'center' },
  modalSubmitText: { color: '#000', fontSize: 14, fontWeight: 'bold' },
});